import { useEffect, useId, useRef, useState } from 'react';
import { ApiError } from '../../identity/types';
import { ErrorSummary, type SummaryIssue } from '../../job-roles/components/ErrorSummary';
import { conflictingRateId, healthcareErrorMessage } from '../healthcareErrors';
import { formatWireMoney, parseCents, parsePercentHundredths } from '../money';
import { describeRateRow, todayIso } from '../rateDisplay';
import {
  HdcErrorCode, differentialLabels, payBasisLabels, rateHelp, rateLabels,
  type CreateServiceRateInput, type DifferentialKind, type PayBasis, type ServiceRate, type ServiceType,
} from '../types';
import { useSupportedPayBases } from '../useSupportedPayBases';
import '../../job-roles/job-roles.css';
import '../healthcare-rates.css';

export interface NamedOption {
  id: string;
  name: string;
}

export interface ServiceRateFormProps {
  /** The form's accessible name, for example "Add a company rate". */
  title: string;
  disciplines: NamedOption[];
  serviceTypes: ServiceType[];
  /** Offered only when a location can narrow the rate (the company schedule). The employee form offers it too. */
  locations: NamedOption[];
  /** The rows already listed, used to show the row a 409 RATE_OVERLAP names. */
  existing: ServiceRate[];
  submitLabel: string;
  /** Preselects a discipline (for example when an employee holds just one). */
  defaultDisciplineId?: string | undefined;
  /** Offers "Any worker category": a company rate that applies to every category without a rate of its own. Only the company schedule sets it; an employee rate always names a category. */
  allowAnyWorkerCategory?: boolean | undefined;
  onSubmit: (input: CreateServiceRateInput) => Promise<void>;
  onCancel: () => void;
}

/** The select value of the "Any worker category" choice (never sent: it becomes `appliesToAnyWorkerCategory`). */
const ANY_CATEGORY = '__any__';

type Key = 'disciplineId' | 'payBasis' | 'rate' | 'differential' | 'effectiveFrom' | 'effectiveTo';

/**
 * One rate row: a discipline, an optional service type and location, a pay basis, a rate, an optional differential and the dates it applies.
 * Used for the company rate schedule and for an employee's own service rates (the shape is the same). Money is parsed from the typed text into
 * integer cents and the differential into integer cents or hundredths of a percent; nothing here multiplies or divides an amount. Rows are never
 * edited in place: to change a rate, end the old row and add a new one.
 */
export function ServiceRateForm({ title, disciplines, serviceTypes, locations, existing, submitLabel, defaultDisciplineId, allowAnyWorkerCategory, onSubmit, onCancel }: ServiceRateFormProps) {
  const uid = useId();
  const payBases = useSupportedPayBases();
  const [disciplineId, setDisciplineId] = useState(defaultDisciplineId ?? '');
  const [serviceTypeId, setServiceTypeId] = useState('');
  const [workLocationId, setWorkLocationId] = useState('');
  const [payBasis, setPayBasis] = useState<PayBasis>('PerVisit');
  const [rate, setRate] = useState('');
  const [kind, setKind] = useState<DifferentialKind>('None');
  const [differential, setDifferential] = useState('');
  const [description, setDescription] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const [effectiveTo, setEffectiveTo] = useState('');
  const [errors, setErrors] = useState<Partial<Record<Key, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [overlap, setOverlap] = useState<{ row: ServiceRate | null; id: string | null; detail: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [focusNonce, setFocusNonce] = useState(0);
  const summaryRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (focusNonce > 0) summaryRef.current?.focus();
  }, [focusNonce]);

  const order: Key[] = ['disciplineId', 'payBasis', 'rate', 'differential', 'effectiveFrom', 'effectiveTo'];
  const issues: SummaryIssue[] = order.filter((k) => errors[k]).map((k) => ({ key: k, message: errors[k]! }));
  const chosenService = serviceTypes.find((t) => t.id === serviceTypeId);
  const fid = (k: string) => `${uid}-${k}`;
  const focusField = (key: string) => formRef.current?.querySelector<HTMLElement>(`[data-field="${key}"]`)?.focus();
  const describe = (k: Key, extra?: string) => [errors[k] ? `${fid(k)}-error` : null, extra].filter(Boolean).join(' ') || undefined;
  const clearError = (k: Key) => setErrors((prev) => { const next = { ...prev }; delete next[k]; return next; });

  async function submit() {
    const found: Partial<Record<Key, string>> = {};
    if (!disciplineId) found.disciplineId = 'Choose a worker category.';
    const cents = parseCents(rate);
    if (rate.trim() === '') found.rate = `Enter the ${rateLabels[payBasis].toLowerCase()}.`;
    else if (cents === null) found.rate = 'Enter the rate as a number with up to 2 decimals, from 0 to 1,000,000.';
    let differentialUnits: number | null = null;
    if (kind === 'Amount' || kind === 'Percent') {
      differentialUnits = kind === 'Amount' ? parseCents(differential) : parsePercentHundredths(differential);
      if (differential.trim() === '') found.differential = kind === 'Amount' ? 'Enter the amount to add, or choose No differential.' : 'Enter the percentage to add, or choose No differential.';
      else if (differentialUnits === null) found.differential = kind === 'Amount' ? 'Enter the differential as an amount from 0 to 1,000,000 with up to 2 decimals.' : 'Enter the differential as a percentage from 0 to 1,000 with up to 2 decimals.';
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom)) found.effectiveFrom = 'Enter the date the rate starts.';
    if (effectiveTo && !/^\d{4}-\d{2}-\d{2}$/.test(effectiveTo)) found.effectiveTo = 'Enter a valid end date, or leave it empty.';
    else if (effectiveTo && effectiveFrom && effectiveTo <= effectiveFrom) found.effectiveTo = 'The end date must be after the start date.';
    setErrors(found);
    setFormError(null);
    setOverlap(null);
    if (Object.keys(found).length > 0) {
      setFocusNonce((n) => n + 1);
      return;
    }
    setBusy(true);
    try {
      const anyCategory = disciplineId === ANY_CATEGORY;
      await onSubmit({
        disciplineId: anyCategory ? null : disciplineId, ...(anyCategory ? { appliesToAnyWorkerCategory: true } : {}), serviceTypeId: serviceTypeId || null, workLocationId: workLocationId || null, payBasis, rateCents: cents!,
        differentialKind: kind, differentialUnits: kind === 'None' ? null : differentialUnits, description: description.trim(), effectiveFrom, effectiveTo: effectiveTo || null,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === HdcErrorCode.RateOverlap) {
        const id = conflictingRateId(err);
        setOverlap({ row: existing.find((r) => r.id === id) ?? null, id, detail: err.message });
      } else {
        setFormError(healthcareErrorMessage(err, 'The rate could not be saved.'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <form ref={formRef} className="hdc-form" aria-label={title} noValidate onSubmit={(e) => { e.preventDefault(); void submit(); }}>
      <ErrorSummary ref={summaryRef} issues={issues} onSelect={focusField} />
      <div className="jr-grid">
        <div className="jr-field">
          <label htmlFor={fid('discipline')}>Worker category <span aria-hidden="true" className="jr-req">*</span></label>
          <select
            id={fid('discipline')} data-field="disciplineId" value={disciplineId} aria-required="true"
            aria-invalid={errors.disciplineId ? true : undefined} aria-describedby={describe('disciplineId', `${fid('discipline')}-hint`)}
            onChange={(e) => { setDisciplineId(e.target.value); clearError('disciplineId'); }}
          >
            <option value="">Choose a worker category</option>
            {allowAnyWorkerCategory && <option value={ANY_CATEGORY}>Any worker category</option>}
            {disciplines.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <p id={`${fid('discipline')}-hint`} className="jr-hint">
            The kind of worker, such as Registered Nurse or Electrician.
            {allowAnyWorkerCategory && disciplineId === ANY_CATEGORY && <> A rate for any worker category is used only when no rate names the worker&apos;s own category.</>}
          </p>
          {errors.disciplineId && <p id={`${fid('disciplineId')}-error`} className="jr-error" role="alert">{errors.disciplineId}</p>}
        </div>
        <div className="jr-field">
          <label htmlFor={fid('service')}>Work type</label>
          <select id={fid('service')} value={serviceTypeId} aria-describedby={`${fid('service')}-hint`} onChange={(e) => setServiceTypeId(e.target.value)}>
            <option value="">Any work type</option>
            {serviceTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <p id={`${fid('service')}-hint`} className="jr-hint">
            The work performed. Leave it as &ldquo;Any work type&rdquo; for a rate that applies to all of a worker category&apos;s work.
            {chosenService?.suggestedPayBasis && <> Suggested pay basis for this work type: {payBasisLabels[chosenService.suggestedPayBasis]} (a hint only).</>}
          </p>
        </div>
        <div className="jr-field">
          <label htmlFor={fid('location')}>Work location</label>
          <select id={fid('location')} value={workLocationId} onChange={(e) => setWorkLocationId(e.target.value)}>
            <option value="">All locations</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
        <div className="jr-field">
          <label htmlFor={fid('basis')}>Pay basis <span aria-hidden="true" className="jr-req">*</span></label>
          <select
            id={fid('basis')} data-field="payBasis" value={payBasis} aria-required="true" aria-describedby={describe('payBasis', `${fid('basis')}-hint`)}
            onChange={(e) => { setPayBasis(e.target.value as PayBasis); clearError('rate'); }}
          >
            {payBases.map((b) => <option key={b} value={b}>{payBasisLabels[b]}</option>)}
          </select>
          <p id={`${fid('basis')}-hint`} className="jr-hint">How the worker is paid for this work. Salary stays on the employee&apos;s base pay.</p>
        </div>
        <div className="jr-field">
          <label htmlFor={fid('rate')}>{rateLabels[payBasis]} (USD) <span aria-hidden="true" className="jr-req">*</span></label>
          <input
            id={fid('rate')} data-field="rate" inputMode="decimal" placeholder="0.00" value={rate} autoComplete="off" aria-required="true"
            aria-invalid={errors.rate ? true : undefined} aria-describedby={describe('rate', `${fid('rate')}-hint`)}
            onChange={(e) => { setRate(e.target.value); clearError('rate'); }}
          />
          <p id={`${fid('rate')}-hint`} className="jr-hint">{rateHelp[payBasis]} What your company pays the worker; it does not come from any payer.</p>
          {errors.rate && <p id={`${fid('rate')}-error`} className="jr-error" role="alert">{errors.rate}</p>}
        </div>
        <div className="jr-field">
          <label htmlFor={fid('kind')}>Differential</label>
          <select id={fid('kind')} value={kind} onChange={(e) => { setKind(e.target.value as DifferentialKind); setDifferential(''); clearError('differential'); }}>
            {(Object.keys(differentialLabels) as DifferentialKind[]).map((k) => <option key={k} value={k}>{differentialLabels[k]}</option>)}
          </select>
          <p className="jr-hint" id={`${fid('kind')}-hint`}>An extra for this work, for example for nights. It is added to the rate.</p>
        </div>
        {kind !== 'None' && (
          <div className="jr-field">
            <label htmlFor={fid('differential')}>{kind === 'Amount' ? 'Differential amount (USD)' : 'Differential percent (%)'} <span aria-hidden="true" className="jr-req">*</span></label>
            <input
              id={fid('differential')} data-field="differential" inputMode="decimal" value={differential} autoComplete="off" aria-required="true"
              aria-invalid={errors.differential ? true : undefined} aria-describedby={describe('differential')}
              onChange={(e) => { setDifferential(e.target.value); clearError('differential'); }}
            />
            {errors.differential && <p id={`${fid('differential')}-error`} className="jr-error" role="alert">{errors.differential}</p>}
          </div>
        )}
        <div className="jr-field">
          <label htmlFor={fid('from')}>Effective from <span aria-hidden="true" className="jr-req">*</span></label>
          <input
            id={fid('from')} data-field="effectiveFrom" type="date" value={effectiveFrom} aria-required="true" aria-invalid={errors.effectiveFrom ? true : undefined}
            aria-describedby={describe('effectiveFrom')} onChange={(e) => { setEffectiveFrom(e.target.value); clearError('effectiveFrom'); clearError('effectiveTo'); }}
          />
          {errors.effectiveFrom && <p id={`${fid('effectiveFrom')}-error`} className="jr-error" role="alert">{errors.effectiveFrom}</p>}
        </div>
        <div className="jr-field">
          <label htmlFor={fid('to')}>Ends before (optional)</label>
          <input
            id={fid('to')} data-field="effectiveTo" type="date" value={effectiveTo} aria-invalid={errors.effectiveTo ? true : undefined}
            aria-describedby={describe('effectiveTo', `${fid('to')}-hint`)} onChange={(e) => { setEffectiveTo(e.target.value); clearError('effectiveTo'); }}
          />
          <p id={`${fid('to')}-hint`} className="jr-hint">The first day the rate no longer applies. Leave empty for a rate with no end date; you can end it later.</p>
          {errors.effectiveTo && <p id={`${fid('effectiveTo')}-error`} className="jr-error" role="alert">{errors.effectiveTo}</p>}
        </div>
        <div className="jr-field jr-grid__wide">
          <label htmlFor={fid('description')}>Note (optional)</label>
          <input id={fid('description')} value={description} maxLength={300} autoComplete="off" onChange={(e) => setDescription(e.target.value)} />
        </div>
      </div>

      {overlap && (
        <div className="hdc-conflict" role="alert" data-testid="rate-overlap">
          <p><strong>This rate overlaps another rate.</strong> Two rates for the same worker category, work type, location and pay basis cannot apply on the same day.</p>
          <p data-testid="rate-overlap-row">
            Conflicting rate: {overlap.row ? <>{describeRateRow(overlap.row)} at {formatWireMoney(overlap.row.rate)}</> : <>row {overlap.id ?? 'unknown'}</>}.
          </p>
          <p>To fix it, end that rate first, or change the dates here so they do not overlap.</p>
        </div>
      )}
      {formError && <p className="jr-error jr-error--block" role="alert">{formError}</p>}
      <div className="jr-actions hdc-form__actions">
        <button type="submit" className="jr-btn jr-btn--primary" disabled={busy}>{busy ? 'Saving…' : submitLabel}</button>
        <button type="button" className="jr-btn jr-btn--secondary" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
